import { http, httpPaged, type PagedResponse } from '@/services/http';

export interface Tag {
  text: string;
}

export interface Project {
  id: number;
  title: string;
  is_public?: boolean;
  tags?: Tag[] | string;
  owner?: unknown;
  [key: string]: unknown;
}

function serializeTags(tags: Tag[] | string | undefined): string | null {
  if (!Array.isArray(tags)) return tags ?? null;
  return tags.map((t) => t.text).join(',');
}

function deserializeTags(project: Project): Project {
  if (typeof project.tags !== 'string') return project;
  const trimmed = project.tags.split(',').map((t) => ({ text: t.trim() }));
  return { ...project, tags: trimmed };
}

export const projectsApi = {
  index(): Promise<Project[]> {
    return http<Project[]>({ url: 'projects', method: 'get' });
  },

  indexPaged(page: number, per: number): Promise<PagedResponse<Project[]>> {
    return httpPaged<Project[]>({ url: 'projects', method: 'get', params: { page, per } });
  },

  search(query: string, page?: number, per?: number): Promise<Project[] | PagedResponse<Project[]>> {
    if (page && per) {
      return httpPaged<Project[]>({
        url: 'projects/search',
        method: 'get',
        params: { query, page, per },
      });
    }
    return http<Project[]>({ url: 'projects/search', method: 'get', params: { query } });
  },

  async get(projectId: number | string): Promise<Project> {
    const project = await http<Project>({ url: `projects/${projectId}`, method: 'get' });
    return deserializeTags(project);
  },

  explore(query: string): Promise<Project[]> {
    return http<Project[]>({ url: 'projects/explore', method: 'get', params: { query } });
  },

  async create(project: Partial<Project>): Promise<Project> {
    const body = { ...project, tags: serializeTags(project.tags) };
    const created = await http<Project>({ url: 'projects', method: 'post', data: body });
    return deserializeTags(created);
  },

  async update(project: Partial<Project> & { id: number }): Promise<Project> {
    const body = { ...project, tags: serializeTags(project.tags) };
    const updated = await http<Project>({
      url: `projects/${project.id}`,
      method: 'patch',
      data: body,
    });
    return deserializeTags(updated);
  },

  remove(projectId: number | string): Promise<void> {
    return http<void>({ url: `projects/${projectId}`, method: 'delete' });
  },

  tags(): Promise<string[]> {
    return http<string[]>({ url: 'projects/tags', method: 'get' });
  },

  share(projectId: number | string, email: string): Promise<unknown> {
    return http({ url: `projects/${projectId}/share`, method: 'post', data: { email } });
  },

  cloneByEmail(projectId: number | string, email: string): Promise<unknown> {
    return http({ url: `projects/${projectId}/clone_by_email`, method: 'post', data: { email } });
  },

  shareForInspection(projectId: number | string, email: string): Promise<unknown> {
    return http({
      url: `projects/${projectId}/share_for_inspection`,
      method: 'post',
      data: { email, role: 'INSPECTOR' },
    });
  },

  unshare(projectId: number | string, email: string): Promise<void> {
    return http<void>({
      url: `projects/${projectId}/unshare`,
      method: 'delete',
      params: { email },
    });
  },

  clone(projectId: number | string): Promise<Project> {
    return http<Project>({ url: `projects/${projectId}/clone`, method: 'post' });
  },

  indexFavorite(userId: number | string): Promise<Project[]> {
    return http<Project[]>({ url: `users/${userId}/favorites/projects`, method: 'get' });
  },

  addFavorite(userId: number | string, projectId: number | string): Promise<unknown> {
    return http({ url: `users/${userId}/favorites/projects/${projectId}`, method: 'post' });
  },

  removeFavorite(userId: number | string, projectId: number | string): Promise<void> {
    return http<void>({
      url: `users/${userId}/favorites/projects/${projectId}`,
      method: 'delete',
    });
  },
};
